import type {
  ParityAlertResponse,
  ParityCheckDetails,
  ParityCheckResult,
  ParityHealthResponse,
  ParityInterval,
  WarmupParityEnriched,
} from '@rhdashboard-vnext/shared';
import { apiFetch } from './client';

// ============================================================
// Parity API client - Fase 6.
//
// Wrappers tipados sobre los endpoints /api/parity/*. Todos aceptan un
// AbortSignal opcional para que el cliente pueda cancelar mid-flight
// (especialmente runParityCheck que toma ~60s upstream).
// ============================================================

// fetchParityStatuses vive en lib/api/sessions.ts (Fase 3, lo importa
// Sessions tab y LiveMonitor). Lo reusamos desde ahi en vez de duplicar.
// Aca solo agregamos los endpoints nuevos de Fase 6.

export async function fetchParityHealth(signal?: AbortSignal): Promise<ParityHealthResponse> {
  return await apiFetch<ParityHealthResponse>('/api/parity/health', { signal });
}

/**
 * Dispara el parity check completo (POST /api/parity/check).
 * El upstream tarda 1-60s segun backlog. signal permite cancelar.
 */
export async function runParityCheck(
  sessionId: string,
  signal?: AbortSignal,
): Promise<ParityCheckResult> {
  return await apiFetch<ParityCheckResult>('/api/parity/check', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
    signal,
  });
}

export async function fetchParityDetails(
  sessionId: string,
  signal?: AbortSignal,
): Promise<ParityCheckDetails> {
  return await apiFetch<ParityCheckDetails>(
    `/api/parity/${encodeURIComponent(sessionId)}/details`,
    { signal },
  );
}

export async function fetchWarmupParity(
  sessionId: string,
  signal?: AbortSignal,
): Promise<WarmupParityEnriched> {
  return await apiFetch<WarmupParityEnriched>(
    `/api/parity/${encodeURIComponent(sessionId)}/warmup`,
    { signal },
  );
}

/**
 * POST de set alert. El upstream devuelve `nextCheck` ISO cuando el interval
 * es '1h'/'2h'/etc (no en 'off'). Lo exponemos para mostrarlo en la UI
 * ("Next: 20:02 ET").
 */
export interface SetParityAlertResponse {
  success?: boolean;
  sessionId: string;
  interval?: ParityInterval;
  status?: string; // upstream devuelve 'off' aca cuando interval==='off'
  active?: boolean;
  nextCheck?: string; // ISO UTC, solo presente cuando alert quedo activo
}

export async function setParityAlert(
  sessionId: string,
  interval: ParityInterval,
  signal?: AbortSignal,
): Promise<SetParityAlertResponse> {
  return await apiFetch<SetParityAlertResponse>(
    `/api/parity/${encodeURIComponent(sessionId)}/alert`,
    {
      method: 'POST',
      body: JSON.stringify({ interval }),
      signal,
    },
  );
}

export async function fetchParityAlert(
  sessionId: string,
  signal?: AbortSignal,
): Promise<ParityAlertResponse> {
  return await apiFetch<ParityAlertResponse>(`/api/parity/${encodeURIComponent(sessionId)}/alert`, {
    signal,
  });
}

export const PARITY_INTERVAL_OPTIONS: ParityInterval[] = ['1h', '2h', '4h', '6h', '12h', 'off'];
